import React, { useState } from 'react';
import { Box, Grow, List, Popper } from '@mui/material';
import { CONSTANTS, PEDIGREE } from 'src/components/Constants';
import MenuItemRow from '../MenuItemRow';
import { FertilityIcon, SpouseIcon } from '../MenuIcons';
import { MENU_TONE, SUBMENU_MODIFIERS } from '../menuTheme';

const FertilityMenu = ({
  node,
  memberRecords,
  setMemberRecords,
  handleClose,
  setPedigreeState
}) => {
  const hasSpouses = node.spouseIds?.length > 0;
  const multipleSpouses = node.spouseIds?.length > 1;

  // Multi-spouse: the union is picked via a NESTED SUBMENU (same interaction as
  // the sibling twin-type submenu). A Select cannot live inside the context-menu
  // Popper — MUI portals its option list to document.body, so choosing an option
  // registers as a click-away and closes the whole menu before the choice lands.
  //
  // The anchor is STATE, not a ref. A ref assignment does not re-render, so the
  // Popper's first render read `anchorEl={null}` and never positioned itself —
  // half of why this submenu never opened at all (see the Popper below).
  const [spouseMenu, setSpouseMenu] = useState({ open: false, spouseId: null });
  const [spouseAnchorEl, setSpouseAnchorEl] = useState(null);
  const openSpouseMenu = (event, spouseId) => {
    setSpouseAnchorEl(event.currentTarget);
    setSpouseMenu({ open: true, spouseId });
  };

  const spouseLabel = (spouseId) => {
    const spouse = memberRecords.find((r) => r.pid === spouseId);
    return spouse
      ? `${spouse.firstName || ''} ${spouse.lastName || ''}`.trim() ||
          `UPN ${spouse.upn || spouseId}`
      : `UPN ${spouseId}`;
  };

  const fertilityOf = (spouseId) =>
    spouseId != null
      ? node.spouseRelationships?.[spouseId]?.fertility ?? null
      : node.fertility ?? null;

  const onChange = (value, spouseId) => {
    const updatedRecords = [...memberRecords];

    const idx = updatedRecords.findIndex((r) => r.pid === node.pid);
    if (idx === -1) return;

    if (hasSpouses && spouseId != null) {
      // Update fertility in spouseRelationships
      const personRels = {
        ...(updatedRecords[idx].spouseRelationships || {})
      };
      personRels[spouseId] = {
        ...(personRels[spouseId] || {}),
        fertility: value
      };
      updatedRecords[idx] = {
        ...updatedRecords[idx],
        fertility: value,
        spouseRelationships: personRels
      };

      // Mirror fertility on the spouse record
      const spouseIdx = updatedRecords.findIndex((r) => r.pid === spouseId);
      if (spouseIdx !== -1) {
        const spouseRels = {
          ...(updatedRecords[spouseIdx].spouseRelationships || {})
        };
        spouseRels[node.pid] = {
          ...(spouseRels[node.pid] || {}),
          fertility: value
        };
        updatedRecords[spouseIdx] = {
          ...updatedRecords[spouseIdx],
          fertility: value,
          spouseRelationships: spouseRels
        };
      }
    } else {
      updatedRecords[idx] = {
        ...updatedRecords[idx],
        fertility: value
      };
    }

    setMemberRecords(updatedRecords);

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    handleClose();
  };

  // Each row carries the mark it actually draws — one crossbar for "No Issue",
  // two for infertile — so the notation is visible before the choice is made.
  // "None" gets an empty gutter, since no mark IS what it means.
  const optionList = (spouseId) => {
    const current = fertilityOf(spouseId);
    return CONSTANTS.fertilityMap.map((option, idx) => (
      <MenuItemRow
        key={idx}
        icon={option.value ? <FertilityIcon value={option.value} /> : null}
        tone={MENU_TONE.person}
        label={option.label}
        selected={option.value === current}
        onClick={() => onChange(option.value, spouseId)}
      />
    ));
  };

  if (!multipleSpouses) {
    return (
      <List disablePadding>
        {optionList(hasSpouses ? node.spouseIds[0] : null)}
      </List>
    );
  }

  return (
    <>
      <List disablePadding>
        {node.spouseIds.map((spouseId) => (
          <MenuItemRow
            key={spouseId}
            icon={<SpouseIcon />}
            tone={MENU_TONE.person}
            label={`with ${spouseLabel(spouseId)}`}
            hasSubmenu
            onMouseEnter={(e) => openSpouseMenu(e, spouseId)}
            onClick={(e) => openSpouseMenu(e, spouseId)}
          />
        ))}
      </List>
      {/* THIS SUBMENU NEVER OPENED. The chevron promised a third level and nothing
          happened — a multi-spouse node could not have a per-union fertility set
          from the menu at all.

          It carried every one of the four things that stop a Popper nested inside
          another Popper from working, and AddRelativeMenu's ChildPartnerMenu had
          already been fixed for exactly this and written the diagnosis down:
          a ref anchor (no re-render, so anchorEl was null on first render),
          `transition` plus a function child, an inner ClickAwayListener that
          swallowed the very click that opened it, and no explicit z-index, which
          would have painted it behind ContextMenu's zIndex-1200 flyout even had it
          opened. This mirrors the working pattern exactly: state anchor, plain
          Grow, zIndex 1300, no transition/function-child, no inner ClickAway.

          Closing is the parent's job, as it is for the sibling and partner menus:
          hovering another row re-anchors it, choosing a value closes the whole
          menu, and ContextMenu's own ClickAwayListener catches a click outside. */}
      <Popper
        open={!!spouseMenu.open}
        anchorEl={spouseAnchorEl}
        placement="right-start"
        sx={{ zIndex: 1300 }}
        modifiers={SUBMENU_MODIFIERS}
      >
        <Grow in={!!spouseMenu.open} style={{ transformOrigin: 'top bottom' }}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 3,
              ml: 0.5
            }}
          >
            <List disablePadding>{optionList(spouseMenu.spouseId)}</List>
          </Box>
        </Grow>
      </Popper>
    </>
  );
};

export default FertilityMenu;
